import { EventEmitter } from '@spotify-internal/emitter';
import { logging } from '@js-sdk/debug-tools';
import { Event } from '../enums/event';
import { Errors } from '../enums/errors';
import { LoggingError } from '../error/logging';
import { default as VERSION } from '../version';
const debugLogger = logging.forTag('playback.logger');
const MAX_SAFE_SEQUENCE_ID = 9007199254740991;
export class Logger extends EventEmitter {
    constructor(options) {
        super();
        /**
         * The current tracking data, set when it is created.
         *
         * Note: When possible, rely on the tracking data that is passed to the actual
         * event. Use this field only if there's possibility of getting the current
         * tracking data.
         */
        this._currentTrackingData = null;
        /**
         * The sequence id for this log.
         */
        this._sequenceId = 0;
        this._sequenceSessionStorage = {};
        this._sessionId = '0';
        this._transport = options.transport;
        this._transportLogger = options.logger;
        this._player = options.player;
        this._init();
    }
    _init() {
        const player = this._player;
        player.on(Event.PLAYER_LOAD, this._onPlayerLoad.bind(this));
        player.on(Event.PLAYER_ERROR, this._onError.bind(this, false));
        player.on(Event.PLAYER_WARNING, this._onError.bind(this, true));
        player.on(Event.PLAYER_PRELOADING_ERROR, this._onError.bind(this, false));
        player.on(Event.PLAYER_TRACKING_DATA_CREATED, this._onTrackingDataCreated.bind(this));
        player.on(Event.PLAYER_TRACKING_DATA_FINALIZED, this._onTrackingDataFinalized.bind(this));
        player.on(Event.PLAYER_PLAYBACK_START, this._onPlaybackStart.bind(this));
    }
    _emitError(error, trackingData) {
        this.emit(Event.LOGGER_ERROR, { error, trackingData });
    }
    _onError(isWarning, event) {
        var _a, _b, _c;
        const data = event.data;
        const trackingData = this._currentTrackingData;
        if (!trackingData || trackingData.noLog) {
            return;
        }
        /**
         * There are internal error properties that exists in some errors but not
         * all errors which we want to capture, so we're using ExtendedCodedError
         * here. We're also marking this as possible undefined to ensure
         * that we can handle cases where we're throwing error events without
         * errors.
         */
        const error = data.error;
        const debug = (_a = error === null || error === void 0 ? void 0 : error.debug) !== null && _a !== void 0 ? _a : {};
        debug.position = 'position' in data ? data.position : 0;
        const errorLogData = {
            track: data.track || {},
            debug: debug,
            preloading: 'preloading' in data ? !!data.preloading : false,
            session_id: this._sessionId,
            playback_id: trackingData.playbackId || null,
            player_play_id: 'playId' in data ? data.playId : null,
            http_status_code: (_b = error === null || error === void 0 ? void 0 : error.status) !== null && _b !== void 0 ? _b : null,
            license_server: (_c = error === null || error === void 0 ? void 0 : error.licenseServer) !== null && _c !== void 0 ? _c : null,
        };
        this._logError(error.code || Errors.UNKNOWN, error, errorLogData, isWarning);
    }
    _onPlayerLoad(e) {
        const logData = e.data.logData;
        if (!logData) {
            return;
        }
        if (logData.impressionURLs) {
            let len = logData.impressionURLs.length;
            while (len--) {
                this._logImpression(logData.impressionURLs[len]);
            }
        }
        else if (logData.impressionURL) {
            this._logImpression(logData.impressionURL);
        }
    }
    _onPlaybackStart(e) {
        const data = e.data.trackingData;
        if (data.noLog || data.noStats) {
            return;
        }
        this._logPlaybackStart(data);
    }
    _onTrackingDataCreated(e) {
        const data = e.data.trackingData;
        if (data.noLog) {
            this._currentTrackingData = null;
            return;
        }
        this._currentTrackingData = data;
        this._setSessionId();
        if (!data.noTSV) {
            this._setSequenceId();
        }
    }
    _setSessionId() {
        this._sessionId = this._transport.getInitTime().toString();
    }
    _setSequenceId() {
        if (this._sessionId) {
            this._sequenceId = this._getSequenceId(this._sessionId);
        }
    }
    _onTrackingDataFinalized(e) {
        const data = e.data.trackingData;
        if (data.noLog) {
            return;
        }
        // Playback was paused in the middle of a song, and now we are at a
        // different session from when playback was started. We will report the
        // end of this song as part of a new session, so we reset the ids for
        // this log.
        const staleSession = this._sessionId !== this._transport.getInitTime().toString();
        if (staleSession) {
            this._setSessionId();
        }
        if (data.playbackId && !data.noTSV) {
            if (staleSession) {
                this._setSequenceId();
            }
            if (!data.playIntended && !data.played) {
                // The track was loaded without an intent to play (perhaps as part of the
                // context reloading or after the context finishes), but was never played.
                // Since there was no intent to play, we do not log a TSV message, and we
                // rollback the sequence id so we don't get gaps.
                this._rollbackSequenceId(this._sessionId);
                return;
            }
            this._logTrackStreamVerification(data);
        }
        if (!data.noStats) {
            this._logPlaybackStats(data);
        }
    }
    _logTrackStreamVerification(data) {
        const verificationData = {
            play_track: data.currentTrackUri,
            playback_id: data.playbackId,
            ms_played: data.msPlayed,
            session_id: this._sessionId,
            sequence_id: this._sequenceId,
            next_playback_id: data.nextPlaybackId,
        };
        debugLogger.debug('Logged TrackStreamVerification', verificationData);
        this._transportLogger
            .logTrackStreamVerification(verificationData)
            .catch((err) => {
            const error = new LoggingError(Errors.TSV_SENDING_FAILED, err.message || 'Unknown reason.');
            this._logError(error.code, err, verificationData);
            this._emitError(error, data);
        });
    }
    _logPlaybackStats(data) {
        const stats = {
            play_track: data.currentTrackUri,
            file_id: data.fileId,
            playback_id: data.playbackId,
            internal_play_id: data.internalPlayId,
            memory_cached: data.memoryCached,
            persistent_cached: data.persistentCached,
            audio_format: data.audiocodec || '',
            video_format: data.videocodec || '',
            manifest_id: data.fileId,
            protected: false,
            isProtected: data.isProtected,
            key_system: data.keySystem,
            key_system_impl: data.keySystemImpl,
            urls_json: JSON.stringify(data.urls),
            start_time: data.loadTime,
            end_time: data.stopTime,
            external_start_time: data.externalLoadTime,
            ms_play_latency: data.msPlayLatency,
            ms_init_latency: data.msInitLatency,
            ms_head_latency: data.msHeadLatency,
            ms_manifest_latency: data.msManifestLatency,
            ms_resolve_latency: data.msResolveLatency,
            ms_license_session_latency: data.msLicenseSessionLatency,
            ms_license_generation_latency: data.msLicenseGenerationLatency,
            ms_license_request_latency: data.msLicenseRequestLatency,
            ms_license_update_latency: data.msLicenseUpdateLatency,
            ms_played: data.msPlayed,
            ms_nominal_played: data.msPlayed,
            ms_file_duration: data.msFileDuration,
            ms_actual_duration: data.msActualDuration,
            ms_start_position: data.startPosition,
            ms_end_position: data.position,
            ms_seek_rebuffer: data.msSeekRebuffering,
            ms_seek_rebuffer_longest: data.maxMsSeekRebuffering,
            ms_stall_rebuffer: data.msStalled,
            ms_stall_rebuffer_longest: data.maxMsStalled,
            n_stalls: data.nStalls,
            n_seekback: data.nSeeksBackward,
            n_seekforward: data.nSeeksForward,
            start_bitrate: data.startBitrate || 0,
            time_weighted_bitrate: data.timeWeightedBitrate,
            reason_start: data.reasonStart,
            reason_end: data.reasonEnd,
            initially_paused: !data.playIntended,
            had_error: data.hadError,
            n_warnings: data.nWarnings,
            n_navigator_offline: data.nOffline,
            session_id: this._sessionId,
            sequence_id: this._sequenceId,
        };
        debugLogger.info('Logging PlaybackStats');
        this._transportLogger.logJSSDKPlaybackStats(stats).catch((err) => {
            const error = new LoggingError(Errors.PLAYBACK_STATS_SENDING_FAILED, err.message || 'Unknown reason.');
            this._logError(error.code, err, stats);
            this._emitError(error, data);
        });
    }
    _logPlaybackStart(data) {
        const payload = {
            play_track: data.currentTrackUri,
            file_id: data.fileId,
            playback_id: data.playbackId,
            session_id: this._sessionId,
            ms_start_position: data.startPosition,
            initially_paused: !data.playIntended,
        };
        debugLogger.info('Logging PlaybackStart');
        this._transportLogger.logJSSDKPlaybackStart(payload).catch((err) => {
            const error = new LoggingError(Errors.PLAYBACK_START_SENDING_FAILED, err.message || 'Unknown reason.');
            this._logError(error.code, err, payload);
            this._emitError(error, data);
        });
    }
    _getSequenceId(key) {
        let sequenceId = this._sequenceSessionStorage[key];
        if (!sequenceId) {
            sequenceId = 0;
        }
        if (sequenceId + 1 >= MAX_SAFE_SEQUENCE_ID) {
            sequenceId = 0;
        }
        this._sequenceSessionStorage[key] = sequenceId + 1;
        return sequenceId;
    }
    _rollbackSequenceId(key) {
        const sequenceId = this._sequenceSessionStorage[key];
        if (sequenceId) {
            this._sequenceSessionStorage[key] = Math.max(sequenceId - 1, 0);
        }
    }
    _logImpression(url) {
        this._transport.request(url, { forget: true }).catch(function (e) {
            debugLogger.warn('Unable to send impression request', e);
        });
    }
    _logError(code, error, jsonData, isWarning) {
        let stackTrace = '';
        if (error) {
            stackTrace = error.stack || '';
            if (!stackTrace) {
                stackTrace = error.toString();
            }
        }
        const data = {
            source: 'playback',
            source_version: VERSION.tagged,
            type: code,
            message: error && error.message,
            stack: stackTrace,
            json_data: jsonData,
            json_data_version: '1.0.0',
        };
        if (isWarning) {
            this._transportLogger
                .logJSSDKWarning(data, 'object')
                .catch((e) => debugLogger.warn('Failed to log warning', data, e));
        }
        else {
            this._transportLogger
                .logJSSDKError(data, 'object')
                .catch((e) => debugLogger.warn('Failed to log error', data, e));
        }
    }
}
Logger.create = (options) => {
    return new Logger(options);
};
//# sourceMappingURL=logger.js.map